import clsx from 'clsx';
import type { WNBATeam, WNBARestProfile } from '../../../types';

function RestDaysChip({ days }: { days?: number }) {
  if (days === undefined) return <span className="text-gray-600">—</span>;
  const colorCls =
    days >= 3 ? 'text-win bg-win/10 border-win/30' :
    days === 2 ? 'text-warn bg-warn/10 border-warn/30' :
    'text-loss bg-loss/10 border-loss/30';
  return (
    <span className={clsx('px-2 py-0.5 rounded border text-xs font-semibold tabular-nums', colorCls)}>
      {days}d rest
    </span>
  );
}

function TagBadge({ label, color = 'warn' }: { label: string; color?: 'warn' | 'loss' | 'neutral' }) {
  const colorCls =
    color === 'loss' ? 'bg-loss/10 border-loss/30 text-loss' :
    color === 'neutral' ? 'bg-surface-3 border-surface-4 text-gray-400' :
    'bg-warn/10 border-warn/30 text-warn';
  return (
    <span className={clsx('px-2 py-0.5 rounded border text-xs font-medium', colorCls)}>
      {label}
    </span>
  );
}

interface RestAnalysisPanelProps {
  awayTeam: WNBATeam;
  homeTeam: WNBATeam;
  awayRest?: WNBARestProfile;
  homeRest?: WNBARestProfile;
}

export function RestAnalysisPanel({ awayTeam, homeTeam, awayRest, homeRest }: RestAnalysisPanelProps) {
  if (!awayRest && !homeRest) {
    return (
      <div className="text-center py-8 text-gray-600 text-sm">
        No rest data available
      </div>
    );
  }

  const hasB2B = awayRest?.isBackToBack || homeRest?.isBackToBack;
  const has2in3 = awayRest?.isSecondInThreeNights || homeRest?.isSecondInThreeNights;

  return (
    <div className="space-y-3">
      {/* Warning banners */}
      {hasB2B && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-loss/5 border border-loss/20 text-xs text-loss">
          <span className="font-semibold">Back-to-Back Alert</span>
          <span className="text-gray-500">—</span>
          <span>
            {[awayRest?.isBackToBack && awayTeam.abbreviation, homeRest?.isBackToBack && homeTeam.abbreviation]
              .filter(Boolean).join(' & ')} on B2B
          </span>
        </div>
      )}
      {!hasB2B && has2in3 && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-warn/5 border border-warn/20 text-xs text-warn">
          <span className="font-semibold">2nd in 3 Nights</span>
          <span className="text-gray-500">—</span>
          <span>
            {[awayRest?.isSecondInThreeNights && awayTeam.abbreviation, homeRest?.isSecondInThreeNights && homeTeam.abbreviation]
              .filter(Boolean).join(' & ')}
          </span>
        </div>
      )}

      {/* Team cards */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { team: awayTeam, rest: awayRest, label: 'Away' },
          { team: homeTeam, rest: homeRest, label: 'Home' },
        ].map(({ team, rest, label }) => (
          <div key={team.id} className="rounded-lg border border-surface-4 bg-surface-2 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-300">{team.abbreviation}</span>
              <span className="text-xs text-gray-600">{label}</span>
            </div>

            <RestDaysChip days={rest?.daysSinceLastGame} />

            <div className="flex flex-wrap gap-1">
              {rest?.isBackToBack && <TagBadge label="B2B" color="loss" />}
              {rest?.isSecondInThreeNights && !rest?.isBackToBack && <TagBadge label="2nd/3nts" color="warn" />}
              {rest?.travelDistanceMiles && rest.travelDistanceMiles > 1000 && (
                <TagBadge label={`${Math.round(rest.travelDistanceMiles / 100) * 100}mi`} color="warn" />
              )}
              {rest?.timezoneChangeHours && Math.abs(rest.timezoneChangeHours) >= 2 && (
                <TagBadge label={`${rest.timezoneChangeHours > 0 ? '+' : ''}${rest.timezoneChangeHours}h TZ`} color="neutral" />
              )}
              {!rest?.isBackToBack && !rest?.isSecondInThreeNights && (
                <TagBadge label="Rested" color="neutral" />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
